/**
 * PlanAyarlariPage.jsx — Sistem Ayarları › Plan Ayarları (backlog #243)
 * Route: /Management/SistemAyarlari/Plan
 * Roller: [1,2]  (yazma yetkisi backend'de: SYSTEM_SETTINGS_MANAGE — Admin, Süper Admin)
 *
 * Kroki / plan ayarları grubu (group=kroki). Şimdilik tek konu: Stand Detay › Konum sekmesinin üç
 * görünümünün kenar payları (#242) — Alan (standın çevresi), Salon (salonun tamamı), Yalnızca Stand.
 * Değerler metre; her yön ayrı. Kaydedilmemiş bir ayar backend'in varsayılanıdır (SystemSettingConstant);
 * "Varsayılana dön" o varsayılanı kaydeder. Her değişiklik geçmişe yazılır (kim, ne zaman, eski → yeni).
 *
 * Yeni bir ayar tipi (NUMBER / BOOLEAN / STRING) eklendiğinde burada kendi düzenleyicisi gerekir;
 * tanınmayan tipler salt okunur gösterilir.
 */

import React, { useEffect, useState } from "react";
import {
  Container, Card, CardBody, CardHeader, Row, Col, Button, Alert, Spinner, Input, Label,
  InputGroup, InputGroupText, Table, Badge,
} from "reactstrap";
import { Save, RotateCcw, History, Info, Ruler } from "lucide-react";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import {
  listSystemSettings, updateSystemSetting, getSystemSettingHistory, SETTING_GROUP_KROKI,
} from "../../../services/systemSettingsService";
import { useTranslation } from "react-i18next";
import i18n, { currentLocaleTag } from "../../../i18n";

const SIDES = [
  { key: "left", get label() { return i18n.t("ayarlar:planAyarlari.sol"); } },
  { key: "top", get label() { return i18n.t("ayarlar:planAyarlari.ust"); } },
  { key: "right", get label() { return i18n.t("ayarlar:planAyarlari.sag"); } },
  { key: "bottom", get label() { return i18n.t("ayarlar:planAyarlari.alt"); } },
];

const fmtNum = (v) => (v == null || v === "" ? "" : String(+Number(v).toFixed(2)));
const fmtDate = (d) => {
  if (!d) return "—";
  const date = new Date(d);
  return Number.isNaN(date.getTime()) ? String(d) : date.toLocaleString(currentLocaleTag());
};
const fmtMargins = (m) =>
  m && typeof m === "object" ? SIDES.map((s) => `${s.label} ${fmtNum(m[s.key])}`).join(" · ") + " m" : "—";

function MarginsEditor({ setting, onSaved }) {
  const { t: tx } = useTranslation("ayarlar");
  const toText = (v) => Object.fromEntries(SIDES.map((s) => [s.key, fmtNum(v?.[s.key])]));
  const [text, setText] = useState(() => toText(setting.value));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  const [history, setHistory] = useState(null);
  const [historyError, setHistoryError] = useState(null);
  // The saved value changed (saved here, or reset): re-seed the inputs during render — React's
  // "adjust state when a prop changes" pattern, not an effect.
  const valueKey = JSON.stringify(setting.value);
  const [seededFor, setSeededFor] = useState(valueKey);
  if (seededFor !== valueKey) {
    setSeededFor(valueKey);
    setText(toText(setting.value));
  }

  const min = setting.min ?? 0;
  const max = setting.max ?? 500;
  const parsed = Object.fromEntries(SIDES.map((s) => [s.key, Number(String(text[s.key]).replace(",", "."))]));
  const invalidSide = SIDES.find((s) => text[s.key] === "" || !Number.isFinite(parsed[s.key])
    || parsed[s.key] < min || parsed[s.key] > max);
  const changed = SIDES.some((s) => Number(setting.value?.[s.key]) !== parsed[s.key]);

  const save = async (value) => {
    setSaving(true);
    setError(null);
    try {
      const updated = await updateSystemSetting(setting.key, value);
      onSaved(updated);
      setSavedAt(new Date());
      if (history) setHistory(await getSystemSettingHistory(setting.key));
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleHistory = async () => {
    if (history) { setHistory(null); return; }
    setHistoryError(null);
    try { setHistory(await getSystemSettingHistory(setting.key)); } catch (e) { setHistoryError(e.message); }
  };

  return (
    <Card className="mb-3">
      <CardHeader className="d-flex align-items-center justify-content-between flex-wrap gap-2 py-2">
        <span className="fw-semibold d-flex align-items-center gap-2">
          <Ruler size={16} /> {setting.label}
          {setting.isDefault && <Badge color="light" className="text-muted border">{tx("planAyarlari.varsayilan")}</Badge>}
        </span>
        <span className="text-muted small">
          {setting.updatedAt ? <>{tx("planAyarlari.sonDegisiklik")}{" "}{fmtDate(setting.updatedAt)}{setting.updatedByName ? ` · ${setting.updatedByName}` : ""}</> : tx("planAyarlari.hicDegistirilmedi")}
        </span>
      </CardHeader>
      <CardBody>
        {setting.description && <p className="text-muted small mb-3">{setting.description}</p>}
        <Row className="g-2">
          {SIDES.map((s) => (
            <Col key={s.key} xs={6} md={3}>
              <Label for={`${setting.key}-${s.key}`} className="small mb-1">{s.label}</Label>
              <InputGroup size="sm">
                <Input
                  id={`${setting.key}-${s.key}`}
                  type="number"
                  inputMode="decimal"
                  min={min}
                  max={max}
                  step={0.5}
                  value={text[s.key]}
                  invalid={invalidSide?.key === s.key}
                  onChange={(e) => { setText((t) => ({ ...t, [s.key]: e.target.value })); setSavedAt(null); }}
                />
                <InputGroupText>m</InputGroupText>
              </InputGroup>
            </Col>
          ))}
        </Row>
        {invalidSide && (
          <div className="text-danger small mt-2">
            {tx("planAyarlari.ileMArasindaBirSayi", { label: invalidSide.label, fmtNum: fmtNum(min), fmtNum2: fmtNum(max) })}
          </div>
        )}
        {error && <Alert color="danger" className="py-2 small mt-3 mb-0">{error}</Alert>}
        <div className="d-flex flex-wrap gap-2 mt-3 align-items-center">
          <Button color="primary" size="sm" disabled={saving || !changed || !!invalidSide} onClick={() => save(parsed)}>
            {saving ? <Spinner size="sm" className="me-1" /> : <Save size={14} className="me-1" />}{tx("common:kaydet")}
          </Button>
          <Button color="light" size="sm" disabled={saving || setting.isDefault}
            title={tx("planAyarlari.varsayilan2", { fmtMargins: fmtMargins(setting.defaultValue) })}
            onClick={() => save(setting.defaultValue)}>
            <RotateCcw size={14} className="me-1" />{tx("planAyarlari.varsayilanaDon")}
          </Button>
          <Button color="link" size="sm" className="ms-auto" onClick={toggleHistory}>
            <History size={14} className="me-1" />{history ? tx("planAyarlari.gecmisiGizle") : tx("planAyarlari.gecmis")}
          </Button>
          {savedAt && <span className="text-success small">{tx("planAyarlari.kaydedildi")}</span>}
        </div>
        {historyError && <Alert color="warning" className="py-2 small mt-3 mb-0">{historyError}</Alert>}
        {history && (
          history.length === 0 ? (
            <div className="text-muted small mt-3">{tx("planAyarlari.buAyarHicDegistirilmedi")}</div>
          ) : (
            <div className="table-responsive mt-3">
              <Table size="sm" className="mb-0 small">
                <thead><tr><th>{tx("planAyarlari.tarih")}</th><th>{tx("planAyarlari.kim")}</th><th>{tx("planAyarlari.eski")}</th><th>{tx("planAyarlari.yeni")}</th></tr></thead>
                <tbody>
                  {history.map((h) => (
                    <tr key={h.id}>
                      <td className="text-nowrap">{fmtDate(h.changedAt)}</td>
                      <td>{h.changedByName || (h.changedBy != null ? `#${h.changedBy}` : "—")}</td>
                      <td>{h.oldValue == null ? <span className="text-muted">{tx("planAyarlari.varsayilan")}</span> : fmtMargins(h.oldValue)}</td>
                      <td>{fmtMargins(h.newValue)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )
        )}
      </CardBody>
    </Card>
  );
}

export default function PlanAyarlariPage() {
  const { t } = useTranslation("ayarlar");
  const [settings, setSettings] = useState(null);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => { document.title = i18n.t("ayarlar:planAyarlari.planAyarlariFds"); }, []);

  useEffect(() => {
    let ignore = false;
    listSystemSettings(SETTING_GROUP_KROKI)
      .then((list) => { if (!ignore) setSettings(list); })
      .catch((e) => { if (!ignore) { setLoadError(e.message); setSettings([]); } });
    return () => { ignore = true; };
  }, []);

  const onSaved = (updated) => setSettings((list) => list.map((s) => (s.key === updated.key ? updated : s)));

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={t("planAyarlari.planAyarlari")} pageTitle={t("planAyarlari.sistemAyarlari")} />
        <Alert color="info" className="d-flex gap-2 align-items-start small">
          <Info size={16} className="flex-shrink-0 mt-1" />
          <div>
            {t("planAyarlari.standDetay")}{" "}<strong>{t("planAyarlari.konum")}</strong>{" "}{t("planAyarlari.sekmesindekiKrokiGorunumlerininKenarPaylari")}
            <strong>{" "}{t("planAyarlari.alan")}</strong>{" "}{t("planAyarlari.gorunumundeStandinCevresindeGosterilecekAlan")}{" "}<strong>{t("planAyarlari.salon")}</strong>{" "}{t("planAyarlari.ve")}
            <strong>{" "}{t("planAyarlari.yalnizcaStand")}</strong>{" "}{t("planAyarlari.gorunumlerindeCerceveninCevresineEklenenBosluk")}
          </div>
        </Alert>
        {loadError && <Alert color="danger">{loadError}</Alert>}
        {settings == null ? (
          <div className="text-center py-5"><Spinner color="primary" /></div>
        ) : settings.length === 0 && !loadError ? (
          <div className="text-muted">{t("planAyarlari.buGruptaAyarYok")}</div>
        ) : (
          settings.map((s) => (s.type === "MARGINS" ? (
            <MarginsEditor key={s.key} setting={s} onSaved={onSaved} />
          ) : (
            <Card key={s.key} className="mb-3">
              <CardHeader className="py-2 fw-semibold">{s.label}</CardHeader>
              <CardBody className="small"><code>{JSON.stringify(s.value)}</code> <span className="text-muted">{t("planAyarlari.buTipIcinDuzenleyiciYok")}</span></CardBody>
            </Card>
          )))
        )}
      </Container>
    </div>
  );
}
